/**
 * Chat with Boris API Service
 *
 * Confluence-grounded people-policy Q&A, answered server-side (proxy-server/lib/policyChat.js).
 */
import { authenticatedFetch } from '../../../shared/utils/authenticatedFetch';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** Mirrors MAX_MESSAGE_CHARS in proxy-server/lib/policyConversation.js. */
export const MAX_QUESTION_CHARS = 1000;
/** Mirrors MAX_HISTORY_TURNS there; the proxy trims to it anyway. */
export const MAX_HISTORY_TURNS = 8;

export interface PolicyChatCitation {
  title: string;
  url: string;
}

export interface PolicyChatResponse {
  answer: string;
  citations: PolicyChatCitation[];
  /** False when the People Confluence did not (fully) answer it — the chat then offers a request. */
  covered?: boolean;
  /** Names this answer for a 👍/👎. Absent on canned replies (errors, not set up). */
  answerId?: string;
}

export interface PolicyChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** The proxy refused the question itself (400) — its message is meant for the person asking. */
export class BorisRefusalError extends Error {}

/**
 * @param history the conversation BEFORE `message`, oldest first — what lets a follow-up such as
 *   "and if I'm part-time?" be understood
 * @throws BorisRefusalError with the proxy's own wording when it refuses the question (e.g. too
 *   long); any other Error for a failure the person can only retry
 */
export async function askBoris(
  message: string,
  history: PolicyChatTurn[] = []
): Promise<PolicyChatResponse> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/people-policy-chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message, history: history.slice(-MAX_HISTORY_TURNS) }),
    // authenticatedFetch throws (and raises the global error banner) on any non-OK status it is
    // not told to pass through. A 400 here is the proxy refusing the question in words meant for
    // the person asking, so it must come back to be read, not be turned into a generic failure.
    suppressErrorStatuses: [400],
  });
  const body = await res.json().catch(() => null);
  if (res.status === 400 && typeof body?.error === 'string') throw new BorisRefusalError(body.error);
  if (!res.ok || typeof body?.answer !== 'string') throw new Error(`Boris answered ${res.status}`);
  return {
    answer: body.answer,
    citations: Array.isArray(body.citations) ? body.citations : [],
    covered: typeof body.covered === 'boolean' ? body.covered : undefined,
    answerId: typeof body.answerId === 'string' ? body.answerId : undefined,
  };
}

export interface BorisRating {
  answerId: string;
  vote: 'up' | 'down';
  comment?: string;
  /** The pair the vote is about — stored server-side only, never sent to analytics. */
  question: string;
  answer: string;
  sources: string[];
  covered?: boolean;
}

const RATING_QUIET_STATUSES = [400, 401, 403, 404, 429, 500, 502, 503, 504];

/** Save a 👍/👎 (proxy-server/lib/borisFeedback.js). Throws on failure; the caller swallows it. */
export async function rateBorisAnswer(rating: BorisRating): Promise<void> {
  const res = await authenticatedFetch(`${PROXY_URL}/api/people-policy-chat/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rating),
    // A rating is telemetry: a failed save must not raise the app-wide error banner in the middle
    // of a chat. The status still comes back, and the caller swallows the throw below.
    suppressErrorStatuses: RATING_QUIET_STATUSES,
  });
  if (!res.ok) throw new Error(`Rating answered ${res.status}`);
}
